import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  EventEmitter,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UsersService, Command } from 'src/app/dashboard/service/users.service';
import { VehicleService } from 'src/app/vehicles/services/vehicle.service';
import { environment } from 'src/environments/environment';
import { MessageService, ConfirmationService } from 'primeng-lts/api';
import { Subscription, timer, of, from } from 'rxjs';
import { takeWhile, delay, tap, filter, map } from 'rxjs/operators';
import { UnitTransmissionStatusService } from 'src/app/units/services/unit-transmission-status.service';
import {
  TransmissionStatus,
  UnitWithTransmissionStatus,
} from 'src/app/units/models/unit-transmission-status';
import Swal from 'sweetalert2';
import {
  BroadcastService,
  CommandPayload,
} from 'src/app/broadcast/service/broadcast.service';

export interface CommandMessage {
  timestamp: string;
  type: string;
  query?: CommandMessageQuery;
  response?: CommandMessageResponse;
}
export interface CommandMessageQuery {
  imei: string;
  plate?: string;
  timestamp?: string;
  payload?: string;
  message?: string;
  ok?: boolean;
  status?: number;
  color?: string;
  background?: string;
}
export interface CommandMessageResponse {
  imei: string;
  timestamp: string;
  message: string;
}

export interface StatusConfig {
  icon: string;
  color: string;
  label: string;
  class: string;
}

export interface CommandUnit {
  label: string;
  value: string;
  imei: string;
  time: string;
  plate?: string;
  protocol?: string;
  transmissionId?: number;
  transmissionName?: string;
  transmissionColor?: string;
}
export interface TransmissionStatusWithUnits {
  label: string;
  value: string | number;
  color: string;
  items: CommandUnit[];
}
export interface Protocol {
  label: string;
  value: string;
}

@Component({
  selector: 'app-command-panel',
  templateUrl: './command-panel.component.html',
  styleUrls: ['./command-panel.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class CommandPanelComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // Outputs
  @Output() close = new EventEmitter<void>();

  // Input opcional - IMEI del dispositivo
  @Input() _imei: string | undefined = undefined;
  // Input opcional - Protocolo del dispositivo
  @Input() _protocol: string | undefined = undefined;
  // Input opcional - commando para el dispositivo
  @Input() _command: string | undefined = undefined;

  // ---------------------------------------------------------------------------
  // Datos del usuario
  _user: { userName: string; userId: string } | undefined = undefined;
  // Listados de IMEIs para filtrar
  _imeis: string[] = [];
  // Datos del dispositivo raw
  _units: any[] = [];
  // Datos de los estados de transmisión
  _transmissionStates: TransmissionStatus[] = [];
  // Observer de transmisión
  _unitsWithTransmissionStatus: Subscription | null = null;
  // Observer de comandos
  _commands: Subscription | null = null;
  // Contraseña para enviar comandos
  _password: string | undefined;
  // ---------------------------------------------------------------------------

  protocols: Protocol[] = [];
  protocol: Protocol | undefined = undefined;

  commands: Command[] = [];
  availableCommands: Command[] = [];
  filteredCommands: Command[] = [];
  selectedCommand: Command | undefined;
  commandPromptSearch: string | undefined;
  loadingCommands: boolean = false;

  sendingCommand: boolean = false;

  units: CommandUnit[] = [];
  onCleanUnitsFliter: boolean = false;
  selectedUnits: CommandUnit[] = [];

  response: CommandMessage | undefined;
  responses: CommandMessage[] = [];

  showFullResponse: boolean = false;
  // ---------------------------------------------------------------------------

  // Estados para el historial
  readonly STATUS_CONFIG: { [key: string]: StatusConfig } = {
    pending: {
      icon: 'pi pi-clock',
      color: '#f59e0b',
      label: 'PENDING',
      class: 'status-pending',
    },
    success: {
      icon: 'pi pi-check',
      color: '#10b981',
      label: 'SUCCESS',
      class: 'status-success',
    },
    error: {
      icon: 'pi pi-times',
      color: '#ef4444',
      label: 'ERROR',
      class: 'status-error',
    },
    offline: {
      icon: 'pi pi-power-off',
      color: '#6b7280',
      label: 'OFFLINE',
      class: 'status-offline',
    },
  };

  constructor(
    private usersService: UsersService,
    private vehicleService: VehicleService,
    private http: HttpClient,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private unitTransmissionStatusService: UnitTransmissionStatusService,
    private broadcastService: BroadcastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.vehicleService.vehicleCompleted
      .pipe(
        filter((T) => T == true),
        tap(() => this.initializeComponent()),
      )
      .subscribe();

    this.broadcastService.responseToCommandsSent
      .pipe(
        tap((item) => console.log('Saints response from Broadcast', item)),
        tap((item) => {
          const res = {
            timestamp:
              new Date(item.timestamp).toLocaleString('es-PE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
              }) || '',
            type: item.type,
            query: item.message
              .map((m: any): CommandMessageQuery | undefined => {
                if (m.query || m.command) {
                  const colors: Record<number, string> = {
                    202: '#3c9600ff',
                    400: '#ffa34dff',
                    404: '#ffa34dff',
                    429: '#ffa34dff',
                    500: '#FF4D4F',
                  };
                  const backgrounds: Record<number, string> = {
                    202: 'honeydew',
                    400: 'oldlace',
                    404: 'oldlace',
                    429: 'oldlace',
                    500: 'lavenderblush',
                  };
                  const messages: Record<number, string> = {
                    202: 'Comando enviado correctamente',
                    400: 'Datos incorrectos',
                    404: 'Unidad fuera de servicio',
                    429: 'Ya hay un comando en ejecución para esta unidad',
                    500: 'No se pudo enviar el comando',
                  };
                  return {
                    imei: m?.command?.device_id || '-',
                    plate: m?.command?.unit || '-',
                    timestamp:
                      new Date(m?.command?.date_client).toLocaleString(
                        'es-PE',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false,
                        },
                      ) || '-',
                    payload: m?.command?.payload || '-',
                    message: messages[m?.query?.status || 500] || '-',
                    ok: m?.query?.ok === true,
                    status: m?.query?.status || 0,
                    color: colors[m?.query?.status || 500],
                    background: backgrounds[m?.query?.status || 500],
                  };
                }
                return undefined;
              })
              .filter(Boolean)[0],
            response: item.message
              .map((m: any): CommandMessageResponse | undefined => {
                if (!m.query && !m.command) {
                  return {
                    imei: m?.device_id || '-',
                    timestamp:
                      new Date(m?.date_server).toLocaleString('es-PE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                      }) || '-',
                    message: m?.message || '-',
                  };
                }
                return undefined;
              })
              .filter(Boolean)[0],
          };
          console.log('saints', res);

          this.response = res;
          this.responses.push(res);
        }),
        tap(() => this.cdr.detectChanges()),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this._unitsWithTransmissionStatus) {
      this._unitsWithTransmissionStatus?.unsubscribe();
    }
    if (this._commands) {
      this._commands?.unsubscribe();
    }
  }

  private initializeComponent(): void {
    this._transmissionStates =
      this.unitTransmissionStatusService.getTransmissionStates();
    this.loadUnits();
    this.loadCommands();
    this.usersService
      .getAll()
      .pipe(
        tap((T: any) => {
          this._user = {
            userName:
              T?.data?.nombre_usuario ||
              localStorage.getItem('user_id') ||
              'unknown',
            userId: T?.data?.id || localStorage.getItem('user_id') || 'unknown',
          };
          this.broadcastService.setUser = this._user;
          this.broadcastService.setPassword = T?.data?.passwordCommandSubmit;
          this._password = T?.data?.passwordCommandSubmit || environment.pwd;
        }),
      )
      .subscribe();
  }
  // Unidades
  // ---------------------------------------------------------------------------
  onLeakedIMEIs(imeis: string[]): void {
    this._imeis = imeis;
    this.updateUnits();
  }
  private unitsToDevices(units: UnitWithTransmissionStatus[]): CommandUnit[] {
    return units
      .filter(
        (u) => u.hasIgnition && [1, 2, 6, 7].includes(u.transmissionStatusId),
      )
      .map((u) => ({
        label: u.unitName,
        value: u.GNSSIMEI,
        imei: u.GNSSIMEI,
        time:
          u.device?.trackerDate?.toLocaleString('es-PE', {
            hour12: false,
          }) || '',
        plate: u.licensePlate,
        protocol: (u.device?.IOs?.type ||
          u.device?.IOs?.typ ||
          u.device?.IOs?.gps ||
          u.device?.deviceName) as string | undefined,
        transmissionId: u.transmissionStatusId,
        transmissionName:
          this._transmissionStates.find(
            (t) => t.transmissionStatusId === u.transmissionStatusId,
          )?.transmissionStatusName || '',
        transmissionColor: u.transmissionStatusColor,
      }));
  }
  private updateUnits(): void {
    let filteredUnits = this.unitsToDevices(this._units);
    if (this._imeis.length > 0) {
      filteredUnits = filteredUnits.filter((u) => this._imeis.includes(u.imei));
    }
    if (this.protocol) {
      filteredUnits = filteredUnits.filter(
        (u) => u.protocol === this.protocol?.value,
      );
    }
    if (this.selectedCommand) {
      filteredUnits = filteredUnits.filter(
        (u) => u.protocol === this.selectedCommand?.protocol,
      );
    }
    this.units = [...filteredUnits];

    this.selectedUnits = [...filteredUnits].filter((u) =>
      this.selectedUnits.some((selected) => selected.value === u.value),
    );
    this.cdr.detectChanges();
  }
  // Left
  // ---------------------------------------------------------------------------
  updateCommands(): void {
    let filteredCommands = this.commands;

    if (this.selectedCommand) {
      filteredCommands = filteredCommands.filter(
        (c) => c.protocol === this.selectedCommand?.protocol,
      );
    }
    if (this.protocol) {
      filteredCommands = filteredCommands.filter(
        (c) => c.protocol === this.protocol?.value,
      );
    }
    this.availableCommands = this.commands
      .map((c) => ({
        ...c,
        disabled: this.protocol?.value
          ? c.protocol !== this.protocol.value
          : false,
      }))
      .sort((a, b) => (a.disabled ? 1 : -1));

    this.filteredCommands = [...filteredCommands];
  }
  onChangeProtocol(): void {
    this.updateUnits();
    this.updateCommands();
  }
  loadUnits(): void {
    this._unitsWithTransmissionStatus =
      this.unitTransmissionStatusService.getUnitsTransmissionStatus
        .pipe(
          filter((T) => T.length > 0),
          tap((T) => {
            this._units = T;
          }),
          map((T) => this.unitsToDevices(T)),
          tap((T) => {
            let filteredUnits = T;
            if (this._imeis.length > 0) {
              filteredUnits = filteredUnits.filter((u) =>
                this._imeis.includes(u.imei),
              );
            }
            if (this.protocol) {
              filteredUnits = filteredUnits.filter(
                (u) => u.protocol === this.protocol?.value,
              );
            }
            if (this.selectedCommand) {
              filteredUnits = filteredUnits.filter(
                (u) => u.protocol === this.selectedCommand?.protocol,
              );
            }
            this.units = filteredUnits;
            this.cdr.detectChanges();
          }),
        )
        .subscribe();
  }
  loadCommands(): void {
    this.loadingCommands = false;
    this._commands = this.usersService
      .getCommands()
      .pipe(
        map((T) => T?.data || []),
        filter((T) => T.length > 0),
        map((T) => {
          if (!Array.isArray(T)) return [];
          const commands: Command[] = T.map((c) => ({
            value: c.value,
            type: c.type,
            protocol: c.protocol,
            description: c.description,
            disabled: c.disabled || false,
            usage_count: c.usage_count,
          }));
          this.protocols = commands.reduce((acc, c) => {
            if (c.protocol && !acc.some((p) => p.value === c.protocol)) {
              acc.push({
                label: c.protocol.toUpperCase(),
                value: c.protocol,
              });
            }
            return acc;
          }, [] as Protocol[]);
          this.commands = commands;
          this.broadcastService.setCommands = commands;
          this.availableCommands = commands;
          this.filteredCommands = commands;
          this.loadingCommands = true;
          this.cdr.detectChanges();
          return [];
        }),
        tap(() => this.onChangeProtocol()),
      )
      .subscribe();
  }
  onCommandChange(event: Command) {
    this.updateUnits();
    this.updateCommands();
  }
  clearCommandSelection() {
    this.selectedCommand = undefined;
    this.updateUnits();
    this.updateCommands();
  }
  clearSelection(): void {
    this.selectedCommand = undefined;
    this.selectedUnits = [];
    this.protocol = undefined;
    this.onCleanUnitsFliter = !this.onCleanUnitsFliter;
    this.updateUnits();
    this.updateCommands();
  }
  sendCommand(): void {
    if (!this.selectedCommand) return;
    if (!this.protocol) return;
    if (!this.selectedUnits.length) return;
    Swal.fire({
      title: 'Confirma el envío del comando',
      text: 'Ingresa tu contraseña para autorizar el envío del comando al dispositivo de seguridad vehicular.',
      input: 'password',
      inputPlaceholder: 'Ingresa tu contraseña',
      inputAttributes: { autocapitalize: 'off' },
      showCancelButton: true,
      confirmButtonText: 'Autorizar y enviar',
      showLoaderOnConfirm: true,
      preConfirm: async (login) => {
        try {
          const response = login == this._password;
          if (!response)
            return Swal.showValidationMessage('Contraseña inválida');
          return response;
        } catch (error) {
          Swal.showValidationMessage(`
        Request failed: ${error}
      `);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        this.sendCommandToUnits();
        this.selectedUnits = [];
      }
    });
    // id: string;
    // timestamp: Date;
    // command: string;
    // description: string;
    // type: string;
    // status: 'pending' | 'success' | 'error' | 'offline';
    // deviceId: string;
    // response?: any;
    // message?: string;

    //     {
    //     "device_id": "861773070128350",
    //     "unit": "ABC-123",
    //     "user_id": 1001,
    //     "user_name": "saints",
    //     "type": "custom",
    //     "payload": "accinfo"
    // }
  }
  sendCommandToUnits() {
    this.showFullResponse = true;
    this.broadcastService.sentCommands(
      [...this.selectedUnits].map(
        (u): CommandPayload => ({
          device_id: u.imei,
          unit: u?.plate || '-',
          user_id: this._user?.userId || '-',
          user_name: this._user?.userName || '-',
          type: this.selectedCommand?.type || '-',
          payload: this.selectedCommand?.value || '-',
        }),
      ),
    );
  }
  clearResponses(): void {
    this.responses = [];
  }
  // Right
  // ---------------------------------------------------------------------------
  filteringCommands(): void {
    const query = this.commandPromptSearch?.trim();
    this.filteredCommands = !query
      ? [...this.availableCommands]
      : this.availableCommands.filter(
          (c) => c.value.includes(query) || c.description.includes(query),
        );
    return;
  }
  commandSelection(command: Command): void {
    this.selectedCommand = command;
  }
  copyCommandToClipboard(command: Command): void {
    navigator.clipboard?.writeText(command.value).then(() => {});
  }
  refreshCommands(): void {
    this.commandPromptSearch = undefined;
    this.loadCommands();
  }
  searchCommand() {
    this.filteredCommands = [...this.availableCommands];
    if (!this.commandPromptSearch?.trim()) return;
    this.filteredCommands = this.availableCommands.filter(
      (c) =>
        c.value.includes(this.commandPromptSearch?.trim() || '') ||
        c.protocol?.includes(this.commandPromptSearch?.trim() || ''),
    );
  }
  selectCommand(command: Command): void {
    this.selectedCommand = command;
    this.updateUnits();
    this.updateCommands();
  }

  // ---------------------------------------------------------------------------

  onClose(): void {
    this.close.emit();
  }
}
